/**
 * Régime de reçu fiscal de l'association de la page de don (chantier
 * « reçus fiscaux Belgique et Luxembourg », 2026-10-30) :
 *  - `cerfa`     : France — « 66 % déductible », reçu par e-mail ;
 *  - `be_281_71` : Belgique — 30 % de réduction, attestation annuelle (281.71),
 *                  dès 40 € de dons par an à l'association ;
 *  - `lu_art112` : Luxembourg — don déduit du revenu imposable (pas de taux) ;
 *  - `nl_anbi`   : Pays-Bas — don à une ANBI, déduit du revenu (pas de taux),
 *                  justificatif après chaque don (2026-11-01) ;
 *  - `de_zuwendung` : Allemagne — don déduit du revenu (Sonderausgaben,
 *                  § 10b EStG), Zuwendungsbestätigung après chaque don
 *                  (2026-11-03).
 *
 * La PAGE le fournit (`fiscalRegime` de l'API, ou le pays de l'association)
 * et les bandeaux le lisent sans le faire descendre de prop en prop. Absent →
 * `cerfa`, l'affichage d'avant : une page qui n'a pas encore l'info ne change
 * jamais pour une association française.
 */
import { computed, inject, provide, toValue, type ComputedRef, type InjectionKey, type MaybeRefOrGetter } from "vue";
import { profilPays, regleRecuFiscal, REGIMES_RECU_FISCAL, type RecuFiscalPays } from "@donoor/utils";

const CLE: InjectionKey<ComputedRef<RecuFiscalPays>> = Symbol("regime-fiscal");

/**
 * À appeler dans la page. `regime` : valeur de l'API (`fiscalRegime`) ;
 * `pays` : repli d'après le pays de l'association quand l'API ne la donne pas.
 */
export function fournirRegimeFiscal(
  regime: MaybeRefOrGetter<string | null | undefined>,
  pays?: MaybeRefOrGetter<string | null | undefined>,
): ComputedRef<RecuFiscalPays> {
  const valeur = computed<RecuFiscalPays>(() => {
    const r = toValue(regime);
    if ((REGIMES_RECU_FISCAL as readonly string[]).includes(r ?? "")) return r as RecuFiscalPays;
    return profilPays(toValue(pays)).recuFiscal ?? "cerfa";
  });
  provide(CLE, valeur);
  return valeur;
}

/** Régime de la page courante (`cerfa` à défaut). */
export function useRegimeFiscal(): ComputedRef<RecuFiscalPays> {
  const fourni = inject(CLE, null);
  return computed(() => fourni?.value ?? "cerfa");
}

/**
 * Taux de réduction à appliquer à l'aperçu « ne vous coûte que » (null : pas
 * de taux — Luxembourg et Pays-Bas déduisent le don du revenu). Règles
 * partagées (@donoor/utils) : 66 % en France, 30 % en Belgique.
 */
export function tauxReductionDuRegime(regime: RecuFiscalPays): number | null {
  const regle = regleRecuFiscal(regime);
  return regle.avantage === "reduction" && regle.tauxReduction !== null ? regle.tauxReduction / 100 : null;
}

/**
 * Déduction du revenu (Luxembourg, Pays-Bas, Allemagne) : part du don que le fisc rend
 * AU MIEUX. L'économie réelle dépend du revenu du donateur : la condition est
 * rappelée en petit sous le montant (Karim, 01/11/2026). null : réduction à
 * taux fixe (France, Belgique) ou pas de taux connu.
 */
export function tauxEconomieMaxDuRegime(regime: RecuFiscalPays): number | null {
  const regle = regleRecuFiscal(regime);
  return regle.avantage === "deduction" && regle.economieMaxPourcent !== null ? regle.economieMaxPourcent / 100 : null;
}

/** Condition de l'économie, en petit sous le montant (null : aucune à rappeler). */
export function cleConditionEconomie(regime: RecuFiscalPays): string | null {
  if (regime === "nl_anbi") return "recusPays.fiscalConditionNl";
  if (regime === "lu_art112") return "recusPays.fiscalConditionLu";
  if (regime === "de_zuwendung") return "recusPays.fiscalConditionDe";
  return null;
}

/**
 * Don DÉDUIT du revenu (Luxembourg, Pays-Bas, Allemagne) : l'économie dépend
 * du taux d'imposition du donateur → la page dit « peut ne vous coûter
 * que … », jamais « ne vous coûte que … » (Karim, 01/11/2026). Réduction
 * d'impôt à taux fixe (France 66 %, Belgique 30 %) : la phrase affirmative.
 */
export function coutAuConditionnel(regime: RecuFiscalPays): boolean {
  return regleRecuFiscal(regime).avantage === "deduction";
}
